import { React, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import './davots.css';
import '../components.css';

function ScrollToTopOnMount() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return null;
}

export function DAVOTS() {
    return (
        <section id="davots" className="components">
            <ScrollToTopOnMount />

            <div className="return">
                <Link to="/">[ Return ]</Link>
            </div>

            <Container fluid className="logos" style={{ marginTop: '60px' }}>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/dbvis-logo.png" alt="DBVIS Logo" />
                    </Col>
                    <Col></Col>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/unikn-logo.png" alt="UNI KN Logo" />
                    </Col>
                </Row>
            </Container>
            <Container fluid>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h1>
                            Interactive Dense Pixel Visualizations for Time Series and Model
                            Attribution Explanations
                        </h1>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <h3>
                            Udo Schlegel<sup>1</sup>, Daniel A. Keim<sup>1</sup>
                        </h3>
                        <h3>
                            <sup>1</sup> University of Konstanz
                        </h3>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col className="text-align-center" sm={10}>
                        <a href="https://diglib.eg.org/handle/10.2312/mlvis20231069">
                            [ Machine Learning Methods in Visualisation for Big Data (MLVis 2023),
                            Eurographics/EuroVis ]
                        </a>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col className="text-align-center">
                        <a href="https://arxiv.org/pdf/2408.15073v1">[ Paper ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://arxiv.org/abs/2408.15073v1">[ arXiv ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://davots.time-series-xai.dbvis.de/">[ Demo ]</a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://github.com/visual-xai-for-time-series/dense-time-series-attributions">
                            [ Code ]
                        </a>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Evaluating explanations from{' '}
                            <span className="bold">
                                Explainable Artificial Intelligence (XAI)
                            </span>{' '}
                            is often not trivial, and differences in applied metrics can be subtle,
                            especially with{' '}
                            <span className="bold">non-intelligible data</span> such as time series.
                            We propose <span className="bold">DAVOTS</span>, an interactive visual
                            analytics approach to explore{' '}
                            <span className="bold">raw time series data</span>,{' '}
                            <span className="bold">activations of neural networks</span>, and{' '}
                            <span className="bold">attributions</span> in a{' '}
                            <span className="bold">dense-pixel visualization</span> to gain insights
                            into the data, the model's decisions, and the explanations.
                        </p>
                        <p>
                            To further support users in exploring large datasets, we apply{' '}
                            <span className="bold">clustering approaches</span> to the visualized
                            data domains to highlight groups and present{' '}
                            <span className="bold">ordering strategies</span> for individual and
                            combined data exploration to facilitate finding patterns. We demonstrate
                            the approach on a CNN trained on the FordA dataset.
                        </p>
                    </Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2023-davots-1/davots-1-attribution-vis.png"
                            alt="From a raw time series through a model and an attribution method to a single dense-pixel row"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Clustering the Data</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            To help users identify patterns, we cluster the raw data and the data
                            produced by the model to derive an{' '}
                            <span className="bold">ordering</span> for the visualization. We focus
                            on <span className="bold">time point clustering</span><sup>[1]</sup>,
                            since our visualization presents individual time points, and implement
                            three distance functions: the{' '}
                            <span className="bold">Euclidean distance</span> as a baseline, the{' '}
                            <span className="bold">
                                normalized Euclidean distance
                            </span>{' '}
                            by Van Wijk and Van Selow<sup>[2]</sup>, and the{' '}
                            <span className="bold">Pearson correlation coefficient</span>.
                        </p>
                        <p>
                            As the density distribution or possible partitions are not always known
                            in advance, we apply{' '}
                            <span className="bold">
                                hierarchical agglomerative clustering
                            </span>{' '}
                            using Ward's method<sup>[3]</sup> and complete linkage, selected based on
                            the neighborhood-preservation measure by Guo and Gahegan<sup>[4]</sup>.
                            Besides grouping the data, the merging order of the hierarchical
                            clustering directly gives us an ordering to visualize the data with
                            similar samples placed near each other.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Dense-Pixel Attribution Visualization on Time Series</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2023-davots-1/davots-1-overview.png"
                            alt="Overview of DAVOTS with the visualization and its adjustable parameters"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            DAVOTS uses as much of the available screen as possible for a{' '}
                            <span className="bold">tabular dense-pixel visualization</span>: rows
                            represent samples, and columns represent data groups. From left to
                            right, each row holds the{' '}
                            <span className="bold">raw time series</span>, a{' '}
                            <span className="bold">histogram</span> of its values, the{' '}
                            <span className="bold">activations</span> of a selected layer with their
                            histogram, the <span className="bold">attributions</span> for the input
                            with their histogram, and lastly the model's{' '}
                            <span className="bold">prediction</span>. Each data type uses a color
                            scale tailored to its properties, e.g., a diverging scale centered on
                            white for time series and attributions to highlight high and low values.
                        </p>
                        <p>
                            The adjustable parameters below the visualization let users{' '}
                            <span className="bold">select the number of visualized samples</span>{' '}
                            with a brushable slider showing the standard deviation of the current
                            ordering, choose the <span className="bold">data stage</span> (train,
                            test, ...), the{' '}
                            <span className="bold">attribution method</span>, and the{' '}
                            <span className="bold">
                                clustering base and clustering method
                            </span>{' '}
                            used for ordering the rows. A tooltip on hover shows the index of a
                            sample, and a highlight around the row helps to keep track of it while
                            comparing across columns.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Preliminary Findings on the FordA Dataset</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2023-davots-1/davots-1-uses-cases.png"
                            alt="Patterns found by clustering on the raw time series and on the activations"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            We explore a CNN trained on the FordA dataset with the Ward method and
                            the normalized Euclidean distance for clustering. Ordering the rows by{' '}
                            <span className="bold">attributions</span>, patterns become visible not
                            only in the attributions but repeatedly in the{' '}
                            <span className="bold">raw time series</span> as well, revealing
                            subsequences that are relevant for the classification.
                        </p>
                        <p>
                            Ordering instead by the{' '}
                            <span className="bold">raw time series</span> produces visually better
                            clusters in the raw data, but the previously found patterns in the
                            attributions become weaker or disappear (U2a), and only occasionally
                            remain visible in both (U2b). Ordering by the{' '}
                            <span className="bold">activations</span> reveals clusters in the raw
                            time series accompanied by noise (U3a), while other clusters again show
                            aligned patterns in the raw time series and the attributions (U3b).
                            Which data type is best suited for clustering therefore depends on what
                            the analyst is looking for.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center">
                    <Col sm={1}></Col>
                    <Col>
                        <p className="references">
                            [1] Aghabozorgi, S., Shirkhorshidi, A. S., & Wah, T. Y. (2015).
                            Time-Series Clustering - A Decade Review. Information Systems, 53,
                            16-38
                        </p>
                        <p className="references">
                            [2] Van Wijk, J. J., & Van Selow, E. R. (1999). Cluster and Calendar
                            Based Visualization of Time Series Data. IEEE Symposium on Information
                            Visualization
                        </p>
                        <p className="references">
                            [3] Ward Jr, J. H. (1963). Hierarchical Grouping to Optimize an
                            Objective Function. Journal of the American Statistical Association,
                            58(301), 236-244
                        </p>
                        <p className="references">
                            [4] Guo, D., & Gahegan, M. (2006). Spatial Ordering and Encoding for
                            Geographic Data Mining and Visualization. Journal of Intelligent
                            Information Systems, 27, 243-266
                        </p>
                        <p className="references">
                            [5] Assaf, R., & Schumann, A. (2019). Explainable Deep Neural Networks
                            for Multivariate Time Series Predictions. IJCAI, 6488-6490
                        </p>
                        <p className="references">
                            [6] Keim, D. A. (2000). Designing Pixel-Oriented Visualization
                            Techniques: Theory and Applications. IEEE Transactions on Visualization
                            and Computer Graphics, 6(1), 59-78
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
            </Container>
        </section>
    );
}
